import { useState, useEffect, useCallback, useRef } from 'react';
import { marketingApi, type MarketingCampaign } from '../services/marketingApi';
import { useAuth } from '../context/AuthContext';
import { getUserCapabilities } from '../utils/userCapabilities';

interface UseMarketingCampaignReturn {
  campaign: MarketingCampaign | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

interface UseMarketingCampaignOptions {
  /**
   * When false, the hook does not fetch or poll for campaigns.
   * Used by App.tsx to disable fetching on public auth routes (signin, forgot-password,
   * reset-password). Without this, unauthenticated requests to the campaign API could
   * trigger 401 → refresh fail → redirect to signin, hijacking e.g. the password-reset
   * flow when the user opens the reset link from email.
   */
  enabled?: boolean;
}

const POLLING_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const useMarketingCampaign = (
  options: UseMarketingCampaignOptions = {}
): UseMarketingCampaignReturn => {
  const { enabled = true } = options;
  const { isAuthenticated, user } = useAuth();
  const [campaign, setCampaign] = useState<MarketingCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  const capabilities = getUserCapabilities(user, isAuthenticated);
  const shouldFetchCampaign = enabled && capabilities.canFetchMarketingCampaign;

  const fetchCampaign = useCallback(async () => {
    // Marketing campaigns only apply to distributor-managed mobile users.
    if (!shouldFetchCampaign) {
      if (isMountedRef.current) {
        setCampaign(null);
        setLoading(false);
      }
      return;
    }

    try {
      setError(null);
      const activeCampaign = await marketingApi.getActiveCampaign();
      
      if (isMountedRef.current) {
        setCampaign(activeCampaign);
        setLoading(false);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error('Failed to fetch campaign'));
        setLoading(false);
        // On error, clear campaign to avoid showing stale data
        setCampaign(null);
      }
    }
  }, [shouldFetchCampaign]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await fetchCampaign();
  }, [fetchCampaign]);

  useEffect(() => {
    isMountedRef.current = true;
    
    // No marketing campaigns unless this is a mobile user tied to a distributor.
    if (!shouldFetchCampaign) {
      setCampaign(null);
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchCampaign();

    // Set up polling
    pollingIntervalRef.current = setInterval(() => {
      if (isMountedRef.current && shouldFetchCampaign) {
        fetchCampaign();
      }
    }, POLLING_INTERVAL);

    // Check on visibility change (app resume)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMountedRef.current && shouldFetchCampaign) {
        fetchCampaign();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check on focus (browser tab focus)
    const handleFocus = () => {
      if (isMountedRef.current && shouldFetchCampaign) {
        fetchCampaign();
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      isMountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchCampaign, shouldFetchCampaign]);

  // Check if campaign has expired
  useEffect(() => {
    if (!campaign) return;

    const checkExpiration = () => {
      const now = new Date();
      const endTime = new Date(campaign.end_time);
      
      if (now > endTime) {
        // Campaign has expired
        setCampaign(null);
      }
    };

    // Check immediately
    checkExpiration();

    // Check every minute
    const expirationCheckInterval = setInterval(checkExpiration, 60 * 1000);

    return () => {
      clearInterval(expirationCheckInterval);
    };
  }, [campaign]);

  return {
    campaign,
    loading,
    error,
    refresh,
  };
};
